import { FormEvent, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { PublicLayout } from '../components/layout/PublicLayout'
import { OccurrenceManagement } from '../components/management/OccurrenceManagement'
import { Icons } from '../components/ui/Icons'
import {
  useEventInvitations,
  useCreateEventInvitation,
  useDeleteEventInvitation,
} from '../hooks/useChurchAdmin'
import { useEvent } from '../hooks/usePotlucks'
import { ApiError } from '../lib/api'
import type { EventInvite } from '@fellowship/shared'

function InviteRow({ invite, onDelete }: { invite: EventInvite; onDelete: (id: string) => void }) {
  const label = invite.inviteeEmail ?? 'Unknown'
  return (
    <div className="flex items-center justify-between px-3.5 py-2.5 rounded-[10px] bg-s2 border border-line-soft">
      <div className="flex-1 min-w-0">
        <div className="text-[13.5px] font-medium text-ink truncate">{label}</div>
        <div className="text-[11.5px] text-muted mt-px capitalize">
          {invite.status}
          {invite.inviteeUserId && (
            <span className="ml-1.5 text-good">· registered user</span>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={() => onDelete(invite.id)}
        aria-label={`Remove invite for ${label}`}
        className="bg-transparent border-0 cursor-pointer text-muted p-1 px-1.5 rounded-md flex items-center"
      >
        <Icons.x size={13} />
      </button>
    </div>
  )
}

function InvitePanel({ eventId }: { eventId: string }) {
  const { data } = useEventInvitations(eventId)
  const createInvite = useCreateEventInvitation(eventId)
  const deleteInvite = useDeleteEventInvitation(eventId)
  const [email, setEmail] = useState('')

  const invitations = data?.data ?? []
  const serverError = createInvite.error instanceof ApiError ? createInvite.error.message : null

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!email.trim()) return
    createInvite.mutate(email.trim(), {
      onSuccess: () => setEmail(''),
    })
  }

  return (
    <div>
      <div className="eyebrow mb-3.5">
        Invitations · {invitations.length}
      </div>

      <form onSubmit={handleSubmit} className="flex gap-2 mb-4">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="guest@example.com"
          aria-label="Email address to invite"
          required
          className="input flex-1 h-10 text-[13.5px]"
        />
        <button
          type="submit"
          disabled={createInvite.isPending}
          className="h-10 px-4 rounded-[10px] border-0 bg-brand-gradient text-white shadow-brand-glow text-[13px] font-semibold cursor-pointer font-sans whitespace-nowrap disabled:opacity-60 disabled:shadow-none"
        >
          {createInvite.isPending ? 'Sending…' : 'Send invite'}
        </button>
      </form>

      {serverError && (
        <div
          role="alert"
          className="px-3 py-2 rounded-lg mb-3 text-[12.5px]"
          style={{ background: '#FAEBE7', border: '1px solid #E9C8C0', color: '#7A2A1E' }}
        >
          {serverError}
        </div>
      )}

      {invitations.length === 0 ? (
        <p className="text-[13px] text-muted m-0">
          No invitations sent yet. Invite people by email above.
        </p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {invitations.map((inv) => (
            <InviteRow
              key={inv.id}
              invite={inv}
              onDelete={(id) => deleteInvite.mutate(id)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default function PersonEventManagePage() {
  const { id, occurrenceId } = useParams<{ id: string; occurrenceId: string }>()
  const { data: eventData } = useEvent(id)
  const isDraft = eventData?.data.status === 'draft'

  return (
    <PublicLayout>
      <div className="max-w-[800px] mx-auto px-6 pt-6 pb-16">
        <Link
          to={`/events/${id}`}
          className="text-[13px] text-muted inline-flex items-center gap-1.5 no-underline mb-5 font-medium"
        >
          <Icons.back size={13} /> Back to event
        </Link>
        <h1 className="serif m-0 mb-7 text-[42px] leading-[1.02] tracking-[-0.025em] font-medium">
          Guest list.
        </h1>
        {occurrenceId && <OccurrenceManagement occurrenceId={occurrenceId} />}

        {id && (
          <div className="mt-10 px-6 py-[22px] bg-surface border border-line-soft rounded-2xl">
            {isDraft ? (
              <div>
                <div className="eyebrow mb-2.5">Invitations</div>
                <p className="text-[13px] text-ink-2 m-0 leading-[1.5]">
                  Publish this event before inviting guests. Invitations link people
                  straight to the event, so they need a published page to land on.
                </p>
                <Link
                  to={`/events/${id}`}
                  className="inline-flex items-center gap-1.5 mt-3.5 text-[13px] font-semibold text-primary no-underline"
                >
                  Go to event to publish <Icons.arrow size={13} />
                </Link>
              </div>
            ) : (
              <InvitePanel eventId={id} />
            )}
          </div>
        )}
      </div>
    </PublicLayout>
  )
}
